import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, GraduationCap, Award, TrendingUp, MapPin, Phone, Calendar, DollarSign, Heart, CheckCircle, ArrowRight, } from 'lucide-react';
import Image from '@/components/ui/Image';
import AnimatedSection from '@/components/ui/AnimatedSection';
import SectionTitle from '@/components/ui/SectionTitle';
import Footer from '@/components/sections/Footer';
import { companyStats, dailySchedule } from '@/data/recruit';
const positions = [
    {
        id: 'electrical',
        title: '電気工事士',
        count: '1名',
        type: '中途',
        description: '商業施設などの屋内外配線、太陽光発電システムの施工・保守、図面／見積作成、重機運転（資格者）',
        requirements: '電気工事士資格保有者優遇（未経験者も応募可）',
    },
    {
        id: 'hvac',
        title: '配管工',
        count: '2名',
        type: '中途',
        description: '空調・衛生・消火配管工事、メンテナンス、図面／見積作成、重機運転（資格者）',
        requirements: '空調・配管工事の経験者優遇（未経験者も応募可）',
    },
    {
        id: 'sales',
        title: '営業職',
        count: '若干名',
        type: '中途',
        description: '法人向け提案営業、既存取引先フォロー、見積・受注管理（施工部門との調整を含む）',
        requirements: '要普通自動車免許、コミュニケーション能力重視',
    },
    {
        id: 'graduate',
        title: '新卒採用',
        count: '若干名',
        type: '新卒',
        description: '電気工事士・配管工を中心に検討（毎年採用予定）',
        requirements: '学歴・経験不問、普通自動車免許（AT可）必須',
    },
];
const careerPath = [
    {
        year: 'Year 0-1',
        title: '基礎習得期',
        description: 'OJT＋外部講習、基礎技能＋初級資格取得',
        icon: GraduationCap,
    },
    {
        year: 'Year 2-3',
        title: '成長期',
        description: '小規模現場リーダー',
        icon: TrendingUp,
    },
    {
        year: 'Year 4-5',
        title: '責任者期',
        description: '現場代理人として複数案件を統括、若手指導／施工管理技士受験',
        icon: Users,
    },
    {
        year: 'Year 6+',
        title: '幹部候補期',
        description: '工事長・営業マネージャー・幹部候補へ登用',
        icon: Award,
    },
];
const benefits = [
    '制服・安全靴支給',
    '消耗品手当支給',
    '退職金制度あり',
    '社会保険完備',
    '各種休暇制度充実',
    '資格取得費用は会社全額負担',
];
const selectionProcess = [
    {
        step: '01',
        title: 'エントリー',
        description: 'エントリーフォーム／書類提出（履歴書・職務経歴書・※HW紹介状）',
    },
    {
        step: '02',
        title: '書類選考',
        description: '書類選考（結果通知2営業日以内）',
    },
    {
        step: '03',
        title: '面接',
        description: '面接1回',
    },
    {
        step: '04',
        title: '内定',
        description: '内定（条件提示・入社日調整）',
    },
];
export default function RecruitPage() {
    const [selectedPosition, setSelectedPosition] = useState(null);
    return (<main className='min-h-screen bg-white'>
      {/* Hero Section */}
      <section className='relative h-[70vh] min-h-[600px] overflow-hidden'>
        <div className='absolute inset-0'>
          <Image src='/images/recruit/recruit-hero.webp' alt='採用情報' className='w-full h-full object-cover' priority/>
          <div className='absolute inset-0 bg-gradient-to-b from-navy/40 via-navy/30 to-navy/60'/>
        </div>

        <div className='relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-24'>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className='text-7xl md:text-8xl text-white font-bebas tracking-wider'>
              RECRUIT
            </h1>
            <p className='mt-6 text-xl md:text-2xl text-white font-noto'>
              採用情報
            </p>
            <p className='mt-4 text-lg text-white/80 max-w-2xl leading-relaxed'>
              「仕事は楽しく」「挑戦を恐れない」をモットーに、
              電気と設備をワンストップで施工できる希少な技術者集団として、
              共に成長していける仲間を募集しています。
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Features */}
      <AnimatedSection className='bg-white'>
        <div className='section-container'>
          <SectionTitle title='WHY JOIN US' subtitle='盛電設備工業で働く理由' outlined/>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mt-12'>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} viewport={{ once: true }} className='bg-lightgray p-8 rounded-lg'>
              <div className='w-16 h-16 rounded-full bg-skyblue/10 flex items-center justify-center mb-6'>
                <Users className='w-8 h-8 text-skyblue'/>
              </div>
              <h3 className='text-xl font-bebas text-navy mb-4'>
                RARE EXPERTISE
              </h3>
              <p className='text-navy/80'>
                電気と設備をワンストップで施工できる希少性。複数の技術領域を習得し、幅広いプロジェクトに携われます。
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }} className='bg-lightgray p-8 rounded-lg'>
              <div className='w-16 h-16 rounded-full bg-skyblue/10 flex items-center justify-center mb-6'>
                <Heart className='w-8 h-8 text-skyblue'/>
              </div>
              <h3 className='text-xl font-bebas text-navy mb-4'>YOUNG TEAM</h3>
              <p className='text-navy/80'>
                平均年齢37歳、少人数で風通しが良い環境。新しいアイデアを積極的に取り入れる活気ある職場です。
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} viewport={{ once: true }} className='bg-lightgray p-8 rounded-lg'>
              <div className='w-16 h-16 rounded-full bg-skyblue/10 flex items-center justify-center mb-6'>
                <TrendingUp className='w-8 h-8 text-skyblue'/>
              </div>
              <h3 className='text-xl font-bebas text-navy mb-4'>
                GROWTH SUPPORT
              </h3>
              <p className='text-navy/80'>
                資格取得費用100%会社負担。実務経験ゼロから国家資格取得・現場リーダーを目指せる体制が整っています。
              </p>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Positions */}
      <AnimatedSection className='bg-gray-50'>
        <div className='section-container'>
          <SectionTitle title='POSITIONS' subtitle='募集職種'/>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mt-12'>
            {positions.map((position, index) => (<motion.div key={position.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className='bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer' onClick={() => setSelectedPosition(selectedPosition === position.id ? null : position.id)}>
                <div className='flex items-start justify-between mb-4'>
                  <div>
                    <div className='flex items-center gap-3 mb-2'>
                      <span className={`px-3 py-1 text-sm rounded-full ${position.type === '新卒'
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700'}`}>
                        {position.type}
                      </span>
                      <span className='text-skyblue font-medium'>
                        {position.count}
                      </span>
                    </div>
                    <h3 className='text-2xl font-bebas text-navy'>
                      {position.title}
                    </h3>
                  </div>
                  <ArrowRight className={`w-5 h-5 text-navy/40 transition-transform ${selectedPosition === position.id ? 'rotate-90' : ''}`}/>
                </div>

                <p className='text-navy/80 mb-4'>{position.description}</p>

                {selectedPosition === position.id && (<motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className='border-t pt-4 mt-4'>
                    <h4 className='font-medium text-navy mb-2'>応募要件</h4>
                    <p className='text-navy/70 text-sm'>
                      {position.requirements}
                    </p>
                  </motion.div>)}
              </motion.div>))}
          </div>

          <div className='mt-12 bg-white p-8 rounded-lg'>
            <h3 className='text-2xl font-bebas text-navy mb-6'>
              BASIC REQUIREMENTS
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <h4 className='font-medium text-navy mb-3'>歓迎条件</h4>
                <ul className='space-y-2 text-navy/80'>
                  <li className='flex items-center'>
                    <CheckCircle className='w-4 h-4 text-green-500 mr-2'/>
                    普通自動車免許（入社後取得可）
                  </li>
                </ul>
              </div>
              <div>
                <h4 className='font-medium text-navy mb-3'>資格条件</h4>
                <ul className='space-y-2 text-navy/80'>
                  <li className='flex items-center'>
                    <CheckCircle className='w-4 h-4 text-skyblue mr-2'/>
                    学歴・経験不問・未経験歓迎
                  </li>
                  <li className='flex items-center'>
                    <CheckCircle className='w-4 h-4 text-skyblue mr-2'/>
                    関連資格（電気／管工事）は入社後取得可
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Career Path */}
      <AnimatedSection className='bg-white'>
        <div className='section-container'>
          <SectionTitle title='CAREER PATH' subtitle='キャリアパス・研修制度' outlined/>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12'>
            {careerPath.map((path, index) => (<motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className='text-center'>
                <div className='w-16 h-16 rounded-full bg-navy mx-auto mb-4 flex items-center justify-center'>
                  <path.icon className='w-8 h-8 text-white'/>
                </div>
                <h3 className='text-lg font-bebas text-navy mb-2'>
                  {path.year}
                </h3>
                <h4 className='font-medium text-navy mb-3'>{path.title}</h4>
                <p className='text-sm text-navy/70'>{path.description}</p>
              </motion.div>))}
          </div>

          <div className='mt-16 bg-lightgray p-8 rounded-lg'>
            <h3 className='text-2xl font-bebas text-navy mb-6'>
              SUCCESS STORY
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
              <div>
                <h4 className='font-medium text-navy mb-3'>若手活躍例</h4>
                <p className='text-navy/80 mb-4'>
                  20代で1級電気施工管理技士／管工事施工管理技士を取得し現場代理人へ
                </p>
                <div className='bg-white p-4 rounded'>
                  <span className='text-2xl font-bebas text-skyblue'>
                    5年目モデル
                  </span>
                  <p className='text-navy font-medium'>
                    月収 42〜50万円（資格・手当込）
                  </p>
                </div>
              </div>
              <div>
                <h4 className='font-medium text-navy mb-3'>資格取得実績</h4>
                <p className='text-navy/80 mb-4'>
                  令和6年度実績：1級電気施工管理技士1名／第一種電気工事士1名合格
                </p>
                <div className='bg-white p-4 rounded'>
                  <span className='text-lg font-medium text-navy'>
                    資格取得支援
                  </span>
                  <p className='text-navy/70'>受験費・講習費を100％会社負担</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Work Environment */}
      <AnimatedSection className='bg-white'>
        <div className='section-container'>
          <SectionTitle title='WORK ENVIRONMENT' subtitle='働く環境' outlined/>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12'>
            <div>
              <h3 className='text-2xl font-bebas text-navy mb-6'>
                DAILY SCHEDULE
              </h3>
              <div className='space-y-4'>
                {dailySchedule.map((item, index) => (<motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: index * 0.1 }} viewport={{ once: true }} className='flex items-center gap-4 p-4 bg-lightgray rounded-lg'>
                    <span className='w-16 text-center font-bebas text-lg text-navy'>
                      {item.time}
                    </span>
                    <span className='text-navy/80'>{item.activity}</span>
                  </motion.div>))}
              </div>
            </div>

            <div>
              <h3 className='text-2xl font-bebas text-navy mb-6'>
                COMPANY STATS
              </h3>
              <div className='grid grid-cols-2 gap-4 mb-8'>
                <div className='bg-lightgray p-6 rounded-lg text-center'>
                  <span className='text-3xl font-bebas text-skyblue'>
                    {companyStats.employees}
                  </span>
                  <p className='text-navy/80 mt-1'>社員数</p>
                </div>
                <div className='bg-lightgray p-6 rounded-lg text-center'>
                  <span className='text-3xl font-bebas text-skyblue'>
                    {companyStats.averageAge}
                  </span>
                  <p className='text-navy/80 mt-1'>平均年齢</p>
                </div>
                <div className='bg-lightgray p-6 rounded-lg text-center'>
                  <span className='text-3xl font-bebas text-skyblue'>
                    {companyStats.certificationRate}%
                  </span>
                  <p className='text-navy/80 mt-1'>資格保有率</p>
                </div>
                <div className='bg-lightgray p-6 rounded-lg text-center'>
                  <span className='text-3xl font-bebas text-skyblue'>
                    {companyStats.foundedYear}
                  </span>
                  <p className='text-navy/80 mt-1'>設立年</p>
                </div>
              </div>

              <h4 className='text-lg font-medium text-navy mb-4'>福利厚生</h4>
              <div className='grid grid-cols-1 gap-2'>
                {benefits.map((benefit, index) => (<div key={index} className='flex items-center'>
                    <CheckCircle className='w-4 h-4 text-green-500 mr-3'/>
                    <span className='text-navy/80'>{benefit}</span>
                  </div>))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Compensation & Benefits */}
      <AnimatedSection className='bg-gray-50'>
        <div className='section-container'>
          <SectionTitle title='COMPENSATION' subtitle='給与・待遇'/>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12'>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} viewport={{ once: true }} className='bg-white p-8 rounded-lg md:col-span-3 lg:col-span-1'>
              <div className='w-16 h-16 rounded-full bg-skyblue/10 flex items-center justify-center mb-6'>
                <DollarSign className='w-8 h-8 text-skyblue'/>
              </div>
              <h3 className='text-xl font-bebas text-navy mb-4'>SALARY</h3>
              <div className='space-y-3'>
                <div>
                  <span className='text-sm text-navy/60'>月給</span>
                  <p className='text-lg font-medium text-navy'>新卒 185,000円～<br />中途 250,000～450,000円</p>
                  <p className='text-sm text-navy/70'>（職種・経験で決定）</p>
                </div>
                <div>
                  <span className='text-sm text-navy/60'>昇給</span>
                  <p className='text-navy/80'>あり</p>
                </div>
                <div>
                  <span className='text-sm text-navy/60'>賞与</span>
                  <p className='text-navy/80'>年2回 8月、12月</p>
                  <p className='text-xs text-navy/60'>（昨年度実績）</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }} className='bg-white p-8 rounded-lg'>
              <div className='w-16 h-16 rounded-full bg-skyblue/10 flex items-center justify-center mb-6'>
                <Calendar className='w-8 h-8 text-skyblue'/>
              </div>
              <h3 className='text-xl font-bebas text-navy mb-4'>HOLIDAYS</h3>
              <div className='space-y-3'>
                <div>
                  <span className='text-sm text-navy/60'>休日</span>
                  <p className='text-navy/80'>週休二日 (毎週)</p>
                </div>
                <div>
                  <span className='text-sm text-navy/60'>年間休日</span>
                  <p className='text-navy/80'>約120日</p>
                </div>
                <div>
                  <span className='text-sm text-navy/60'>有給休暇</span>
                  <p className='text-navy/80'>10日（半年後付与）</p>
                </div>
                <div>
                  <span className='text-sm text-navy/60'>ライフサポート休暇</span>
                  <p className='text-navy/80'>あり</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} viewport={{ once: true }} className='bg-white p-8 rounded-lg'>
              <div className='w-16 h-16 rounded-full bg-skyblue/10 flex items-center justify-center mb-6'>
                <Award className='w-8 h-8 text-skyblue'/>
              </div>
              <h3 className='text-xl font-bebas text-navy mb-4'>ALLOWANCES</h3>
              <div className='space-y-3'>
                <div>
                  <span className='text-sm text-navy/60'>諸手当</span>
                  <p className='text-navy/80'>資格手当</p>
                  <p className='text-xs text-navy/60'>
                    
                  </p>
                </div>
                <div>
                  <span className='text-sm text-navy/60'>資格取得支援</span>
                  <p className='text-navy/80'>受験費・講習費100%負担</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Selection Process */}
      <AnimatedSection className='bg-white'>
        <div className='section-container'>
          <SectionTitle title='SELECTION PROCESS' subtitle='選考フロー' outlined/>

          <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mt-12'>
            {selectionProcess.map((step, index) => (<motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className='text-center relative'>
                <div className='w-16 h-16 rounded-full bg-navy mx-auto mb-4 flex items-center justify-center'>
                  <span className='text-white font-bebas text-xl'>
                    {step.step}
                  </span>
                </div>
                <h3 className='text-lg font-bebas text-navy mb-3'>
                  {step.title}
                </h3>
                <p className='text-sm text-navy/70'>{step.description}</p>
                {index < selectionProcess.length - 1 && (<div className='hidden md:block absolute top-8 left-full w-full'>
                    <ArrowRight className='w-6 h-6 text-navy/30 mx-auto'/>
                  </div>)}
              </motion.div>))}
          </div>

          <div className='mt-16 bg-lightgray p-8 rounded-lg'>
            <h3 className='text-2xl font-bebas text-navy mb-6'>
              APPLICATION REQUIREMENTS
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
              <div>
                <h4 className='font-medium text-navy mb-3'>必要書類</h4>
                <ul className='space-y-2 text-navy/80'>
                  <li>• 履歴書（写真付）</li>
                  <li>• 職務経歴書</li>
                  <li>• ハローワーク紹介状（自主応募は不要）</li>
                </ul>
              </div>
              <div>
                <h4 className='font-medium text-navy mb-3'>面接方法</h4>
                <p className='text-navy/80'>
                  原則本社で対面
                </p>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Contact CTA */}
      <AnimatedSection className='bg-navy'>
        <div className='section-container'>
          <div className='text-center'>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h2 className='text-6xl md:text-7xl font-bebas tracking-wider text-white'>
                JOIN OUR TEAM
              </h2>
              <p className='text-xl font-noto text-white/80 mt-6 max-w-2xl mx-auto'>
                一緒に「いい空気」を作りませんか？
                ご質問やエントリーはお気軽にお問い合わせください。
              </p>

              <div className='mt-12 flex flex-col sm:flex-row gap-4 justify-center'>
                <Link to='/contact' className='btn-secondary'>
                  エントリー・お問い合わせ
                </Link>
                <a href='tel:019-681-3977' className='btn-outline border-white text-white hover:bg-white hover:text-navy'>
                  <Phone className='w-4 h-4 mr-2'/>
                  019-681-3977
                </a>
              </div>

              <div className='mt-8 text-white/60 text-sm'>
                <p>受付時間: 平日 9:00〜18:00</p>
                <p className='mt-1'>
                  <MapPin className='w-4 h-4 inline mr-1'/>
                  〒020-0839 岩手県盛岡市津志田南3-13-15
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </main>);
}
